import React from "react";

import { Spinner, TextEditor } from "@performyard/py-components";

import NotesDropdownHeader from "./NotesDropdownHeader";

import type { NotesData } from "../types";

import { config } from "../config";

type PublicNotesSectionProps = {
	notesData: NotesData;
	isNotesLoading: boolean;
	isNotesDisabled: boolean;
	showPrimaryPublicNotes: boolean;
	publicNoteContent: string;
	showPublicNotesSaved: boolean;
	showPublicNotesSaving: boolean;
	isPublicNotesError: boolean;
	onPublicNotesOptionSelect: (optionId: "my-notes" | "participant-notes" | "meeting-transcript") => void;
	onPublicNoteChange: (value: string) => void;
};

/**
 * Public notes section with dropdown to switch between participants' notes.
 * Displays the shared notes editor with autosave status.
 */
const PublicNotesSection = ({
	notesData,
	isNotesLoading,
	isNotesDisabled,
	showPrimaryPublicNotes,
	publicNoteContent,
	showPublicNotesSaved,
	showPublicNotesSaving,
	isPublicNotesError,
	onPublicNotesOptionSelect,
	onPublicNoteChange,
}: PublicNotesSectionProps): React.ReactElement => {
	const currentNoteValue = showPrimaryPublicNotes ? publicNoteContent : notesData.secondaryPublicNotes?.content || "";

	return (
		<div className="flex flex-col gap-2">
			<NotesDropdownHeader
				selectedOption={showPrimaryPublicNotes ? "my-notes" : "participant-notes"}
				showSaved={showPublicNotesSaved}
				showSaving={showPublicNotesSaving}
				onOptionSelect={onPublicNotesOptionSelect}
				hasError={isPublicNotesError}
			/>
			{isNotesLoading ? (
				<div className="flex items-center justify-center py-8">
					<Spinner size={config.spinnerSize.medium} />
				</div>
			) : (
				<TextEditor
					id="public-notes-editor"
					initialValue={currentNoteValue}
					onChange={onPublicNoteChange}
					placeholder="Add shared notes..."
					readOnly={isNotesDisabled || !showPrimaryPublicNotes}
					minHeight={config.layout.textEditorMinHeight}
					maxHeight={config.layout.textEditorMaxHeight}
				/>
			)}
		</div>
	);
};

export default PublicNotesSection;
